import { useEffect, useRef, useState } from "react";
import { useAuthStore, selectIsAuthenticated } from "@safers/auth";
import { useEventsStore } from "@/stores/events.store";
import { canSeeEvent } from "@/lib/eventVisibility";
import type { EventMessage, EventSeverity, EventSiteInfo } from "@/services/types";

const ALARM_SEVERITIES: ReadonlySet<EventSeverity> = new Set<EventSeverity>([
  "WARNING",
  "CRITICAL",
]);
const AUTO_DISMISS_MS = 8000;

function alarmKey(e: EventMessage): string {
  return `${e.eventId}|${e.severity ?? ""}`;
}

export function useEventAlarm() {
  const [alarm, setAlarm] = useState<EventMessage | null>(null);
  const [site, setSite] = useState<EventSiteInfo | null>(null);
  const seenRef = useRef<string | null>(null);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);

  /*
    배너는 라우트 밖(`App`)에 있어 로그아웃해도 안 사라진다. 여기서 안 지우면 앞 계정의
    알람이 로그인 화면과 다음 사람의 첫 화면에 남고, 누르면 그 사람에겐 403 이다.

    effect 가 아니라 **렌더 중에 조정**한다 — effect 는 그린 뒤에 돌아 앞 계정의 알람이
    한 프레임 스쳐 보인다. 로그아웃 순간에 남의 알람이 비치면 안 된다.
    (`pages/cctv-ai/index.tsx` 의 lastSiteId 와 같은 결)
  */
  const [prevAuth, setPrevAuth] = useState(isAuthenticated);
  if (prevAuth !== isAuthenticated) {
    setPrevAuth(isAuthenticated);
    if (!isAuthenticated) {
      setAlarm(null);
      setSite(null);
    }
  }

  // 본 알람 표시는 ref 라 렌더 중에 못 건드린다. 상태와 달리 한 프레임 늦어도
  // 문제없다 — 다음 알람이 오기 전에만 비워지면 된다.
  useEffect(() => {
    if (!isAuthenticated) seenRef.current = null;
  }, [isAuthenticated]);

  useEffect(() => {
    const initial = useEventsStore.getState().stompEvents[0];
    seenRef.current = initial ? alarmKey(initial) : null;

    const unsub = useEventsStore.subscribe((state) => {
      const latest = state.stompEvents[0];
      if (!latest || alarmKey(latest) === seenRef.current) return;
      seenRef.current = alarmKey(latest);

      // 스토어에서 직접 읽는다 — 훅 인자로 받으면 로그인 상태가 바뀔 때마다 재구독하고
      // 그 사이 알람을 흘린다.
      if (
        !canSeeEvent(useAuthStore.getState().user, {
          category: latest.category,
          siteId: latest.site?.id ?? null,
        })
      ) {
        return;
      }

      if (!latest.severity || !ALARM_SEVERITIES.has(latest.severity)) return;
      setAlarm(latest);
      setSite(latest.site ?? null);
    });

    return unsub;
  }, []);

  useEffect(() => {
    if (!alarm) return;
    const t = setTimeout(() => setAlarm(null), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [alarm]);

  return { alarm, site, dismiss: () => setAlarm(null) };
}
